import { CloseCircleFilled, EditFilled, SaveFilled } from '@ant-design/icons'
import { Avatar, Button, ConfigProvider, Spin, message } from 'antd'
import { useForm } from 'antd/es/form/Form'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { alertColorType } from '../../../../constants/alertStyleVariable'
import { displayMessage } from '../../../../helpers/Message'
import {
  addPatientAlertConfiguration,
  errorPatientAlertConfiguration,
  getPatientAlertConfiguration,
  selectErrorInPatientAlertConfiguration,
  selectPatientAlertConfigurationByKey,
  selectSuccessInPatientAlertConfiguration,
  successPatientAlertConfiguration,
} from '../../../../state/patientAlertConfiguration/patientAlertConfigurationReducer'
import {
  getSinglePatientAlert,
  selectSinglePatientAlertsById,
} from '../../../../state/singlePatientAlert/singlePatientAlertReducer'
import AlertConfigurationForm from '../../../Forms/AlertConfigurationForm/AlertConfigurationForm'
import SinglePatientAlertsCollapse from '../../../SinglePatientAlertsCollapse/SinglePatientAlertsCollapse'
import { usePatientTabContext } from '../../PatientTabs'
import { usePatientContext } from '../../PatientsView'
import { TabEmptyText } from '../../PatientsViewStyle'
import {
  AlertConfigureCollapse,
  AlertConfigureCollapseTitle,
  AlertTabConfigContainer,
  AlertTabContainer,
  AlertTabHeader,
  ConfigureAlertContainer,
} from './AlertTabStyle'

type AlertTabProps = {
  patientKey: string
}

function AlertTab({ patientKey }: AlertTabProps) {
  const dispatch = useDispatch()
  const configureCollapseKey = 'configureCollapse'
  const [updateState, setUpdateState] = useState(false)
  const [configureCollapseActiveKey, setConfigureCollapseActiveKey] =
    useState<string>()
  const [configureAlertForm] = useForm()
  const [messageApi, contextHolder] = message.useMessage()
  const { activeKey, size } = usePatientTabContext()
  const { setOpenViewStates, configureAlertViewState } = usePatientContext()
  const alertsData = useSelector(selectSinglePatientAlertsById(patientKey))!
  const alertConfiguration = useSelector(
    selectPatientAlertConfigurationByKey(patientKey)
  )
  const patientError = useSelector(selectErrorInPatientAlertConfiguration)
  const patientSuccess = useSelector(selectSuccessInPatientAlertConfiguration)

  const onFormFinish = () => {
    const {
      firstAlertNurseKey,
      firstNumber,
      escalationAlertNurseKey,
      secondNumber,
      firstAlertDoctorKey,
      doctorNumber,
    } = configureAlertForm.getFieldsValue()

    // Validate Nurse 1
    if (firstAlertNurseKey && !firstNumber) {
      configureAlertForm.setFields([
        {
          name: 'firstNumber',
          errors: ['Please select a phone number for the selected nurse.'],
        },
      ])
      return
    }

    // Validate Nurse 2
    if (escalationAlertNurseKey && !secondNumber) {
      configureAlertForm.setFields([
        {
          name: 'secondNumber',
          errors: ['Please select a phone number for the selected nurse.'],
        },
      ])
      return
    }

    // Validate Doctor
    if (firstAlertDoctorKey && !doctorNumber) {
      configureAlertForm.setFields([
        {
          name: 'doctorNumber',
          errors: ['Please select a phone number for the selected doctor.'],
        },
      ])
      return
    }

    dispatch(
      addPatientAlertConfiguration({
        alertConfiguration: configureAlertForm.getFieldsValue(),
        patientKey: patientKey,
      })
    )
  }

  const updateEvent = () => {
    configureAlertForm.submit()
  }

  const onCancel = () => {
    setUpdateState(false)
    configureAlertForm.setFieldsValue(alertConfiguration)
  }

  useEffect(() => {
    dispatch(getSinglePatientAlert(patientKey))
    dispatch(getPatientAlertConfiguration(patientKey))
  }, [dispatch, patientKey])

  useEffect(() => {
    if (alertConfiguration) {
      configureAlertForm.setFieldsValue(alertConfiguration)
    }
  }, [alertConfiguration, configureAlertForm])

  useEffect(() => {
    if (configureAlertViewState) {
      setUpdateState(true)
      setConfigureCollapseActiveKey(configureCollapseKey)
    } else {
      setUpdateState(false)
      setConfigureCollapseActiveKey('')
    }
  }, [configureAlertViewState])

  useEffect(() => {
    if (activeKey !== '3') {
      setUpdateState(false)
      setOpenViewStates((prev) => ({
        ...prev,
        [patientKey]: {
          ...prev[patientKey],
          configureAlertState: false,
        },
      }))
    }
  }, [activeKey, patientKey, setOpenViewStates])

  useEffect(() => {
    if (patientError) {
      configureAlertForm.setFieldsValue(alertConfiguration)
      dispatch(errorPatientAlertConfiguration(null))
      displayMessage(patientError, 'error', messageApi)
    }
    if (patientSuccess) {
      setUpdateState(false)
      dispatch(successPatientAlertConfiguration(null))
      displayMessage(patientSuccess, 'success', messageApi)
    }
  }, [
    alertConfiguration,
    configureAlertForm,
    dispatch,
    messageApi,
    patientError,
    patientSuccess,
  ])

  return (
    <>
      <AlertTabConfigContainer>
        {contextHolder}
        <AlertConfigureCollapse
          size={size}
          accordion
          onChange={(value) => {
            let key = value as string
            setConfigureCollapseActiveKey(key)
          }}
          activeKey={configureCollapseActiveKey}
          items={[
            {
              key: configureCollapseKey,
              label: (
                <AlertConfigureCollapseTitle>
                  Configure
                </AlertConfigureCollapseTitle>
              ),
              children: (
                <>
                  <AlertTabHeader>
                    <div className='alertConfigUpdateContainer'>
                      {!updateState ? (
                        <Button
                          onClick={() => setUpdateState(true)}
                          size='small'
                          className='iconButton'
                        >
                          {' '}
                          <EditFilled className='anticon' /> Update
                        </Button>
                      ) : (
                        <>
                          <Button onClick={onCancel} size='small'>
                            <CloseCircleFilled className='anticon' /> Cancel
                          </Button>
                          <Button onClick={updateEvent} size='small'>
                            <SaveFilled className='anticon' /> Save
                          </Button>
                        </>
                      )}
                    </div>
                  </AlertTabHeader>
                  <ConfigureAlertContainer>
                    <AlertConfigurationForm
                      defaultValue={alertConfiguration}
                      onFinish={onFormFinish}
                      size={size}
                      disabledState={!updateState}
                      form={configureAlertForm}
                    />
                  </ConfigureAlertContainer>
                </>
              ),
            },
          ]}
        />
      </AlertTabConfigContainer>
      <AlertTabContainer>
        <AlertTabHeader>
          <div className='alertColorTagContainer'>
            <div className='alertColorTag'>
              <Avatar
                size={'small'}
                shape='circle'
                style={{ background: alertColorType['Critical'] }}
              />
              Critical
            </div>
            <div className='alertColorTag'>
              <Avatar
                size={'small'}
                shape='circle'
                style={{ background: alertColorType['Urgent'] }}
              />
              Urgent
            </div>
            <div className='alertColorTag'>
              <Avatar
                size={'small'}
                shape='circle'
                style={{ background: alertColorType['Warning'] }}
              />
              Warning
            </div>
          </div>
        </AlertTabHeader>
        {!alertsData ? (
          <Spin />
        ) : (
          <>
            {!alertsData.patientAlertData.length ? (
              <TabEmptyText>No Alerts Data</TabEmptyText>
            ) : (
              <ConfigProvider
                theme={{
                  components: {
                    Collapse: {
                      headerBg: 'transparent',
                    },
                  },
                }}
              >
                <SinglePatientAlertsCollapse
                  alertsData={alertsData.patientAlertData}
                  size={size}
                />
              </ConfigProvider>
            )}
          </>
        )}
      </AlertTabContainer>
    </>
  )
}

export default AlertTab
